import { useRef, useState, useCallback } from "react";

interface UseSpeechInputOptions {
  lang?: string;
  continuous?: boolean;
}

interface UseSpeechInputReturn {
  isRecording: boolean;
  isSupported: boolean;
  start: (currentValue?: string) => void;
  stop: () => void;
  toggle: (currentValue?: string) => void;
}

/**
 * Shared hook for browser Web Speech API input.
 * Calls `onTranscript` with the full accumulated text (prepended after existing value).
 * Works independently of field focus — caller routes the transcript to the right state.
 */
export function useSpeechInput(
  onTranscript: (text: string) => void,
  options: UseSpeechInputOptions = {},
): UseSpeechInputReturn {
  const { lang = "en-US", continuous = true } = options;
  const recognitionRef = useRef<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const baseRef = useRef("");

  const SpeechRecognition =
    typeof window !== "undefined"
      ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
      : null;

  const isSupported = !!SpeechRecognition;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, []);

  const start = useCallback(
    (currentValue = "") => {
      if (!SpeechRecognition) return;
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = continuous;
      recognition.interimResults = true;
      recognition.lang = lang;

      baseRef.current = currentValue.trimEnd();
      let finalTranscript = "";

      recognition.onresult = (event: any) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        const base = baseRef.current;
        const separator = base ? " " : "";
        const combined = base + separator + finalTranscript + (interim ? "\u200B" + interim : "");
        onTranscript(combined);
      };

      recognition.onend = () => {
        setIsRecording(false);
        const base = baseRef.current;
        const separator = base ? " " : "";
        const clean = (base + separator + finalTranscript).replace(/\u200B/g, "");
        onTranscript(clean);
      };

      recognitionRef.current = recognition;
      recognition.start();
      setIsRecording(true);
    },
    [SpeechRecognition, continuous, lang, onTranscript],
  );

  const toggle = useCallback(
    (currentValue = "") => {
      if (isRecording) {
        stop();
      } else {
        start(currentValue);
      }
    },
    [isRecording, start, stop],
  );

  return { isRecording, isSupported, start, stop, toggle };
}
